import React from "react"
import axios from "axios"

import { styled, keyframes } from "@mui/material/styles"
import { css } from '@emotion/react';
import { logEvent } from "../utils/analytics"
import snackbarStore from "../stores/snackbarStore"
import { Provider } from "mobx-react"
import SFSnackbar from "./SFSnackbar"

import _ from "lodash"
import {
  Tab,
  Tabs,
  Button,
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
  Slide,
  Fade,
  Grid2
} from "@mui/material"
import Starter from "images/starter.png"
import Producer from "images/producer.png"
import Professional from "images/professional.png"
import { TransitionGroup } from "react-transition-group"

const PricingTableContainer = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  align-items: center;
  margin-bottom: 48px;
`

const PricingTableRow = styled('div')`
  display: flex;
  justify-content: center;
`

const PriceContainer = styled('div')(({ isHighlighted }) => css`
  width: 312px;
  padding: 34px 36px 28px;
  border-radius: 12px;
  background-color: ${isHighlighted ? "hsla(0,0%,10%,.05)" : "inherit"};
  border: ${isHighlighted ? "1px solid hsla(0,0%,10%,.1)" : "inherit"};
`);

const SwitchToAnnualLink = styled('p')`
  opacity: 0.5;
  font-size: 12px;
  cursor: pointer;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`


const SFButton = styled(Button)`
  width: 100%;
  margin-top: 16px;
`

const SFTabs = styled(Tabs)`
  .MuiTabs-scroller {
    margin-bottom: 24px !important;
    border: 1px solid;
    border-radius: 10px;
    border-color: hsla(0, 0%, 10%, 0.1);
  }

  .MuiTabs-indicator {
    height: 47px;
    background-color: rgb(115, 113, 247);
    border-radius: 10px;
  }

  .MuiTabs-flexContainer {
    background-color: hsla(0, 0%, 10%, 0.05);
  }
`

const SFTab = styled(Tab)(({ isSelected }) => css`
  font-size: 16px;
  width: 116px;
  &:focus {
    background-color: transparent;
  }

  color: ${isSelected ? 'white' : 'inherit'} !important;
  opacity: ${isSelected ? 'inherit' : '0.7'};
  z-index: 10;
`);

const badgeHighlightShimmer = keyframes`
  0% {
    left: -100%;
    top: -150px;
  }
  50% {
    left:100%;
    top: 150px;
  }
  to {
    left:100%;
    top: 150px;
  }
`

const Badge = styled('span')(({ isHighlighted }) => css`
  height: 25px;
  padding: 2px 6px;
  text-align: center;
  border-radius: 4px;
  background-color: ${isHighlighted ? "white" : "transparent"};
  color: #545a69;
  display: table;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;

  &:before {
    animation: ${badgeHighlightShimmer} 3.5s;
    animation-delay: 1.8s;
    animation-iteration-count: infinite;
    animation-timing-function: ease-out;
    background: linear-gradient(
      180deg,
      rgba(130, 130, 130, 0) 0%,
      rgba(130, 130, 130, 0.2) 25%,
      rgba(130, 130, 130, 0.3) 50%,
      rgba(130, 130, 130, 0.2) 75%,
      rgba(130, 130, 130, 0) 100%
    );
    content: "";
    display: block;
    height: 90px;
    left: -60%;
    position: absolute;
    top: -150px;
    transform: rotate(-25deg);
    width: 200px;
    display: ${isHighlighted ? "inherit" : "none"};
  }
`);

const stores = {
  snackbarStore,
}

export default class PricingTable extends React.Component {
  static defaultProps = {
    initialValueOfToggle: "yearly",
    showToggle: true,
    parityPricing: null
  }

  constructor(props) {
    super(props)
    this.csrfToken = props.csrfToken
    this.priceContainerRef = React.createRef(null)
    this.state = {
      showYearlyPrices: props.initialValueOfToggle == "yearly",
      valueOfToggle: props.initialValueOfToggle
    }
  }

  handleToggleSelection = (event, valueOfToggle) => {
    if (valueOfToggle == "yearly") {
      this.setState({ valueOfToggle: valueOfToggle, showYearlyPrices: true })
    } else {
      this.setState({ valueOfToggle: valueOfToggle, showYearlyPrices: false })
    }
  }

  handleSubscribeClick = async (price_id, product_id) => {
    axios.defaults.headers.common["X-CSRF-Token"] = this.csrfToken
    let { createCheckoutSessionPath, couponId, utmSource } = this.props

    if (utmSource) {
      createCheckoutSessionPath += `?utm_source=${utmSource}`
    }

    logEvent("Subscribe click", {utmSource: utmSource, price_id: price_id, couponId: couponId})

    try {
      const response = await axios({
        method: "post",
        url: createCheckoutSessionPath,
        timeout: 10000,
        data: {
          authenticity_token: this.csrfToken,
          price_id: price_id,
          product_id: product_id,
          coupon_id: couponId,
        },
      })
      const checkoutSessionUrl = response.data["checkout_session_url"]
      window.location.href = checkoutSessionUrl
      return true
    } catch (error) {
      snackbarStore.showSnackbar(`We couldn't process your subscription request. Please try again or contact support. Error: ${error.response.data["error"]}`, "error")
      console.error("Create checkout session error", error)
      return false
    }
  }

  renderPrice(plan, idx) {
    const isDiscounted = Boolean(plan.discounted_price)
    const { createCheckoutSessionPath, couponId, parityPricing } = this.props
    const { showYearlyPrices } = this.state

    let planTitle
    let imageTitle
    switch (plan.credit_amount) {
      case 100:
        planTitle = "Starter"
        imageTitle = Starter
        break
      case 300:
        planTitle = "Producer"
        imageTitle = Producer
        break
      case 600:
        planTitle = "Professional"
        imageTitle = Professional
        break
      default:
        console.error("Unsupported plan")
        break
    }

    let currencyCode = "USD"
    let conversionRate = 1.0
    let priceFactor = 1.0
    if (parityPricing?.currency_code) {
      currencyCode = parityPricing.currency_code
      conversionRate = parseFloat(parityPricing.usd_conversion_rate) || 1.0
    }
    if (parityPricing?.show_discount && parityPricing?.discount_percentage) {
      const pricePercentageOff = parseFloat(parityPricing.discount_percentage)
      priceFactor = 1 - (pricePercentageOff / 100)
    }

    const shouldRoundToWholeDollars = conversionRate !== 1.0;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: shouldRoundToWholeDollars ? 0 : 2,
      maximumFractionDigits: shouldRoundToWholeDollars ? 0 : 2,
    })

    const roundedFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })

    let formattedPrice, formattedTotalYearlyPrice
    if (plan.is_yearly) {
      let price = (plan.price / 100 / 12) * conversionRate * priceFactor;
      if (shouldRoundToWholeDollars) {
        price = Math.round(price);
      }
      formattedPrice = formatter.format(price);

      let totalYearlyPrice = (plan.price / 100) * conversionRate * priceFactor;
      if (shouldRoundToWholeDollars) {
        totalYearlyPrice = Math.round(totalYearlyPrice);
      }
      formattedTotalYearlyPrice = roundedFormatter.format(totalYearlyPrice);
    } else {
      let price = (plan.price / 100) * conversionRate * priceFactor;
      if (shouldRoundToWholeDollars) {
        price = Math.round(price);
      }
      formattedPrice = formatter.format(price);
    }

    let formattedDiscountedPrice, formattedTotalYearlyDiscountedPrice
    if (isDiscounted) {
      let discountedPrice = (plan.discounted_price / 100) * conversionRate * priceFactor;
      if (shouldRoundToWholeDollars) {
        discountedPrice = Math.round(discountedPrice);
      }
      formattedDiscountedPrice = formatter.format(discountedPrice);

      if (plan.is_yearly) {
        let monthlyDiscountedPrice = (plan.discounted_price / 100 / 12) * conversionRate * priceFactor;
        if (shouldRoundToWholeDollars) {
          monthlyDiscountedPrice = Math.round(monthlyDiscountedPrice);
        }
        formattedDiscountedPrice = formatter.format(monthlyDiscountedPrice);

        let totalYearlyDiscountedPrice = (plan.discounted_price / 100) * conversionRate * priceFactor;
        if (shouldRoundToWholeDollars) {
          totalYearlyDiscountedPrice = Math.round(totalYearlyDiscountedPrice);
        }
        formattedTotalYearlyDiscountedPrice = roundedFormatter.format(totalYearlyDiscountedPrice);
      }
    }

    const isHighlighted = plan.credit_amount === 300
    const pricePerSample = plan.is_yearly
      ? plan.price / (plan.credit_amount * 12)
      : plan.price / plan.credit_amount

    let yearlySavingsAmount

    if (plan.is_yearly) {
      const { plansWithDiscount } = this.props
      const equivalentMonthlyPlan = _.find(plansWithDiscount, function (p) {
        return !p.is_yearly && p.credit_amount == plan.credit_amount
      })
      if (isDiscounted) {
        yearlySavingsAmount =
          equivalentMonthlyPlan.price * 12 - plan.discounted_price
      } else {
        yearlySavingsAmount = equivalentMonthlyPlan.price * 12 - plan.price
      }
    }

    return (
      <Grid2 item size={{xl: 4, lg: 4, md: 4, sm: 6, xs: 12}} key={plan.price_id}>
        <StyledEngineProvider injectFirst>
          <TransitionGroup>
            <Fade
              timeout={750}
              style={{ transitionDelay: `${(idx + 1) * 100}ms` }}
            >
              <div css={css`
                display: flex;
                justify-content: center;
              `}>
                <TransitionGroup>
                  <Slide
                    container={this.priceContainerRef.current}
                    style={{ transitionDelay: `${(idx + 1) * 100}ms` }}
                  >
                    <PriceContainer isHighlighted={isHighlighted}>
                      <Badge isHighlighted={isHighlighted}>
                        {isHighlighted && <span>Most popular</span>}
                      </Badge>
                      <img
                        src={imageTitle}
                        alt={`${planTitle} plan image`}
                        css={css`
                          border-radius: 6px;
                          margin-bottom: 16px;
                          height: 120px;
                          max-width: 100%;
                        `}
                      />
                      <p
                        css={css`
                          font-weight: 600;
                          font-size: 20px;
                          margin-top: 0;
                          margin-bottom: 12px;
                        `}
                      >
                        {planTitle}
                      </p>
                      <p
                        css={css`
                          opacity: 0.5;
                          font-size: 16px;
                          font-weight: 600;
                        `}
                      >
                        {plan.credit_amount} Monthly Sample Credits
                      </p>
                      {isDiscounted && (
                        <div
                          css={css`
                            font-weight: 700;
                            font-size: 36px;
                            margin-right: 8px;
                            font-size: 18px;
                            opacity: 0.7;
                            text-decoration: ${isDiscounted
                              ? "line-through"
                              : "inherit"};
                          `}
                        >
                          {formattedPrice}
                        </div>
                      )}
                      <div
                        css={css`
                          display: flex;
                          align-items: flex-end;;
                        `}
                      >
                        <div
                          css={css`
                            font-weight: 700;
                            font-size: 36px;
                            margin-right: 4px;
                          `}
                        >
                          {formattedDiscountedPrice
                            ? formattedDiscountedPrice
                            : formattedPrice}
                        </div>
                        <div>
                          {plan.is_yearly &&
                          <>
                            {!couponId && <div
                            css={css`
                              opacity: 0.5;
                              font-size: 13px;
                            `}>
                              2+ months off
                            </div>}
                            <div
                              css={css`
                                opacity: 0.5;
                                font-size: 13px;
                              `}>
                              {formattedTotalYearlyDiscountedPrice
                              ? formattedTotalYearlyDiscountedPrice
                              : formattedTotalYearlyPrice} billed annually
                            </div>
                          </>
                          }
                          <div
                            css={css`
                              font-size: 13px;
                              font-weight: 600;
                              margin-bottom: 8px;
                            `}
                          >
                            per month<br />
                          </div>
                        </div>
                      </div>
                      {createCheckoutSessionPath && (
                        <>
                          <SFButton
                            color="primary"
                            variant="contained"
                            onClick={() =>
                              this.handleSubscribeClick(plan.price_id, plan.product_id)
                            }
                            type="submit"
                            aria-label={`Subscribe to the ${planTitle} for ${
                              plan.is_yearly
                                ? formattedTotalYearlyDiscountedPrice || formattedTotalYearlyPrice
                                : formattedDiscountedPrice || formattedPrice
                            }`}
                          >
                            Subscribe
                          </SFButton>
                          {!showYearlyPrices &&
                            <SwitchToAnnualLink
                              css={css`
                                opacity: 0.6;
                                font-size: 14px;
                              `}
                              onClick={(e) => this.handleToggleSelection(e, "yearly")}>
                                Save with yearly (2+ months off) ↗
                            </SwitchToAnnualLink>}
                        </>
                      )}
                    </PriceContainer>
                  </Slide>
                </TransitionGroup>
              </div>
            </Fade>
          </TransitionGroup>
        </StyledEngineProvider>
      </Grid2>
    )
  }

  render() {
    const { showYearlyPrices, valueOfToggle } = this.state
    const { plansWithDiscount, showToggle } = this.props

    const filteredPlans = _.filter(plansWithDiscount, function (plan) {
      return plan.is_yearly == showYearlyPrices
    })

    const theme = createTheme({
      typography: {
        fontFamily: ["Lato", "Open Sans"].join(","),
        color: "black",
      },
      palette: {
        primary: {
          main: "#7471f7",
        },
      },
    })

    return (
      <ThemeProvider theme={theme}>
        <Provider {...stores}>
          <PricingTableContainer ref={this.priceContainerRef}>
            {showToggle && (
              <div
                css={css`
                  position: relative;
                `}
              >
                <SFTabs
                  value={valueOfToggle}
                  onChange={this.handleToggleSelection}
                  aria-label="Switch between monthly and yearly prices - use left and right arrow keys to navigate"
                >
                  <SFTab
                    disableRipple
                    label="Monthly"
                    value="monthly"
                    isSelected={valueOfToggle == "monthly"}
                    aria-label="Monthly pricing option"
                  />
                  <SFTab
                    disableRipple
                    label="Yearly"
                    value="yearly"
                    isSelected={valueOfToggle == "yearly"}
                    aria-label="Yearly pricing option"
                  />
                </SFTabs>
                <div
                  css={css`
                    position: absolute;
                    padding: 2px 8px;
                    border-radius: 5px;
                    background-color: #fff59d;
                    top: -12px;
                    right: -52px;
                    font-weight: 600;
                  `}
                >
                  20% OFF
                </div>
              </div>
            )}
            <PricingTableRow>
              <Grid2 container spacing={2} justifyContent="center">
                {_.map(filteredPlans, (plan, idx) => {
                  return this.renderPrice(plan, idx)
                })}
              </Grid2>
            </PricingTableRow>
          </PricingTableContainer>
          <SFSnackbar />
        </Provider>
      </ThemeProvider>
    )
  }
}
